import { useEffect, useRef } from "react";

import { logError } from "../lib/errors";

export const FOREGROUND_REFRESH_MIN_INTERVAL_MS = 15_000;

type ForegroundRefreshOptions = {
  enabled?: boolean;
  minIntervalMs?: number;
};

export function useForegroundRefresh(
  refresh: () => void | Promise<void>,
  {
    enabled = true,
    minIntervalMs = FOREGROUND_REFRESH_MIN_INTERVAL_MS,
  }: ForegroundRefreshOptions = {}
): void {
  const refreshRef = useRef(refresh);
  const inFlightRef = useRef(false);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!enabled) return;

    let disposed = false;
    let lastRefreshAt = Date.now();

    const refreshIfStale = () => {
      const now = Date.now();
      if (disposed || inFlightRef.current || now - lastRefreshAt < minIntervalMs) return;

      lastRefreshAt = now;
      inFlightRef.current = true;
      Promise.resolve()
        .then(() => refreshRef.current())
        .catch((error: unknown) => logError(error))
        .finally(() => {
          inFlightRef.current = false;
        });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshIfStale();
    };

    window.addEventListener("focus", refreshIfStale);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      disposed = true;
      window.removeEventListener("focus", refreshIfStale);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, minIntervalMs]);
}
